import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon } from './Icon';
import s from './Pill.module.css';

export type Tone = 'neutral' | 'brand' | 'pos' | 'neg' | 'warn' | 'info' | 'outline';

export interface PillProps {
  tone?: Tone;
  size?: 'xs' | 'sm' | 'md';
  icon?: string;
  children: ReactNode;
  title?: string;
  className?: string;
}

/** A small rounded label: statuses, directions, weights, tags. */
export function Pill({ tone = 'neutral', size = 'sm', icon, children, title, className }: PillProps) {
  return (
    <span className={cx(s.pill, s[tone], s[size], className)} title={title}>
      {icon && <Icon name={icon} size={size === 'md' ? 16 : 13} />}
      {children}
    </span>
  );
}

export type Direction = 'long' | 'short' | 'both' | string;

/** LONG / SHORT, coloured; muted where the strategy is not active. */
export function DirectionPill({
  direction,
  size = 'sm',
  muted,
}: {
  direction: Direction | null | undefined;
  size?: 'xs' | 'sm';
  muted?: boolean;
}) {
  if (!direction) return null;
  const d = direction.toLowerCase();
  const tone: Tone = muted ? 'outline' : d === 'long' ? 'pos' : d === 'short' ? 'neg' : 'info';
  return (
    <Pill tone={tone} size={size}>
      {d === 'both' ? 'BOTH' : d.toUpperCase()}
    </Pill>
  );
}

/** A coloured dot with an optional "live" glow (running, queued). */
export function StatusDot({ tone = 'neutral', live }: { tone?: Tone; live?: boolean }) {
  return <span className={cx(s.dot, s[`dot_${tone}`], live && s.live)} aria-hidden />;
}

export function StatusLabel({ tone = 'neutral', live, children }: { tone?: Tone; live?: boolean; children: ReactNode }) {
  return (
    <span className={cx(s.status, s[`text_${tone}`])}>
      <StatusDot tone={tone} live={live} />
      {children}
    </span>
  );
}

/** Keyboard key hint, e.g. `/`. */
export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className={s.kbd}>{children}</kbd>;
}
